// client/src/components/RouteBar.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';

export default function RouteBar() {
  const { user, membership, profile, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  const hasActive = Boolean(membership && membership.has_active_membership);

  const allNavItems = useMemo(() => {
    const items = [];

    // Estate Sales pipeline items only for active members
     if (hasActive) {
      items.push(
        { path: '/estate-sales-pipeline/uploader', label: 'Upload Pictures' },
        { path: '/estate-sales-pipeline/inventory', label: 'Inventory Dashboard' },
        { path: '/estate-sales-pipeline/UserDashboard', label: 'User Dashboard' },
        { path: '/estate-sales-pipeline/sold-inventory', label: 'Sales Tracker' },
        { path: '/estate-sales-pipeline/member-usage-log', label: 'Activity Log' }
      );
    }

    // "Update Profile" and "Update Membership" links for any authenticated client
    if (user && profile) {
      // These are only shown in the main nav if the client is not an active member
      if (!hasActive) {
        items.push(
          { path: '/estate-sales-pipeline/update-profile', label: 'Update Account' },
          { path: '/estate-sales-pipeline/member-maint2', label: 'Update Membership' }
        );
      }
    }

    return items;
  }, [hasActive, user, profile]);

  const navItems = allNavItems;

  const brandVars = {
    '--scucci-blue-500': '#2563EB',
    '--scucci-blue-600': '#1D4ED8',
    '--scucci-blue-700': '#1E40AF',
  };

  const barRef = useRef(null);
  const [barHeight, setBarHeight] = useState(56);
  useEffect(() => {
    const update = () => barRef.current && setBarHeight(barRef.current.clientHeight);
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const [navOpen, setNavOpen] = useState(false);
  const [acctOpen, setAcctOpen] = useState(false);
  const navFirstRef = useRef(null);
  const acctFirstRef = useRef(null);

  useEffect(() => { setNavOpen(false); setAcctOpen(false); }, [location.pathname]);

  useEffect(() => {
    const onKey = (e) => (e.key === 'Escape') && (setNavOpen(false), setAcctOpen(false));
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);
  useEffect(() => { if (navOpen && navFirstRef.current) navFirstRef.current.focus(); }, [navOpen]);
  useEffect(() => { if (acctOpen && acctFirstRef.current) acctFirstRef.current.focus(); }, [acctOpen]);

  const desktopLinkClass = ({ isActive }) =>
    [
      'relative px-3.5 py-2 rounded-full text-[15px] font-semibold',
      'outline-none focus:ring-2 focus:ring-white/70',
      isActive
        ? 'text-white shadow-[inset_0_-1px_0_rgba(255,255,255,0.15)]'
        : 'text-white/95 hover:text-white hover:shadow-[inset_0_-1px_0_rgba(255,255,255,0.08)] hover:bg-white/10',
      'transition-colors motion-safe:duration-200'
    ].join(' ');

  return (
    <>
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:bg-white focus:text-black focus:px-3 focus:py-2 focus:rounded-md z-[60]">
        Skip to main content
      </a>

      <nav
        ref={barRef}
        role="navigation"
        aria-label="Main"
        style={brandVars}
        className={[
          'fixed top-0 inset-x-0 z-50 text-white border-b border-white/10',
          'bg-gradient-to-r from-[var(--scucci-blue-700)] via-[var(--scucci-blue-600)] to-[var(--scucci-blue-600)]',
          'supports-[backdrop-filter]:bg-white/5 supports-[backdrop-filter]:backdrop-blur-xl',
          'shadow-[0_1px_0_0_rgba(255,255,255,0.06)_inset,0_8px_20px_-10px_rgba(0,0,0,0.35)]'
        ].join(' ')}
      >
        <div className="max-w-screen-xl mx-auto w-full px-3 sm:px-4">
          <div className="h-14 min-h-[56px] flex items-center gap-3">
            <div className="md:hidden">
              <button
                type="button"
                aria-label="Open menu"
                aria-haspopup="dialog"
                aria-expanded={navOpen}
                onClick={() => setNavOpen(true)}
                className="inline-flex h-10 w-10 items-center justify-center rounded-xl hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/70 transition-colors"
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M4 6h16M4 12h16M4 18h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
            </div>

            <Link
              to="/"
              aria-label="Home"
              className="shrink-0 inline-flex items-center gap-2 rounded-xl px-2 py-2 hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/70"
            >
              <img src="/images/scucci-core-icon.png" alt="" className="h-8 w-8" loading="lazy" />
              <span className="hidden sm:inline text-lg font-bold tracking-tight drop-shadow">Scucci</span>
            </Link>

            <div className="hidden md:flex items-center gap-2 flex-1">
              {navItems.map((item) => (
                <NavLink key={item.path} to={item.path} end className={desktopLinkClass}
                  style={({ isActive }) => isActive ? { backgroundColor: 'var(--scucci-blue-500)' } : undefined}
                >
                  <span className="relative">
                    {item.label}
                    <span className="pointer-events-none absolute -bottom-1 left-0 w-full h-[2px] rounded-full bg-white/0 motion-safe:transition-all"
                      style={{ backgroundColor: 'transparent' }}
                    />
                  </span>
                </NavLink>
              ))}
            </div>

            <div className="ml-auto flex items-center gap-2">
             

              <button
                type="button"
                aria-label="Open account menu"
                aria-haspopup="dialog"
                aria-expanded={acctOpen}
                onClick={() => setAcctOpen(true)}
                className="inline-flex h-10 w-10 items-center justify-center rounded-xl hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/70 transition-colors"
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M12 11a4 4 0 1 0-4-4 4 4 0 0 0 4 4Zm0 2c-4.7 0-8.5 2.35-8.5 5.2A1.8 1.8 0 0 0 5.3 20h13.4a1.8 1.8 0 0 0 1.8-1.8C20.5 15.35 16.7 13 12 13Z" stroke="currentColor" strokeWidth="1.6" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div
        className={(navOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none') +
          ' fixed inset-0 z-[60] bg-black/50 motion-safe:transition-opacity duration-200'}
        onClick={() => setNavOpen(false)}
        aria-hidden="true"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
        className={[
          'fixed top-0 left-0 z-[61] h-full w-[92%] max-w-sm',
          'bg-white text-gray-900 shadow-2xl',
          'rounded-r-2xl border border-gray-100',
          'motion-safe:transition-transform motion-safe:duration-300',
          navOpen ? 'translate-x-0' : '-translate-x-full'
        ].join(' ')}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <img src="/images/scucci-core-icon.png" alt="" className="h-7 w-7" />
            <span className="text-xl font-semibold">Menu</span>
          </div>
          <button
            type="button"
            aria-label="Close menu"
            onClick={() => setNavOpen(false)}
            className="h-10 w-10 inline-flex items-center justify-center rounded-lg hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>
        <nav className="px-2 py-3">
          <p className="px-4 py-2 text-xs font-semibold uppercase tracking-widest text-gray-500">Primary</p>
          <ul className="flex flex-col">
            {navItems.map((item, idx) => (
              <li key={item.path}>
                <NavLink
                  ref={idx === 0 ? navFirstRef : null}
                  to={item.path}
                  end
                  className={({ isActive }) =>
                    [
                      'block rounded-xl px-4 py-4 text-[17px] font-semibold outline-none',
                      'focus:ring-2 focus:ring-blue-500',
                      isActive ? 'bg-blue-50 text-blue-700' : 'hover:bg-gray-50'
                    ].join(' ')
                  }
                >
                  {item.label}
                </NavLink>
              </li>
            ))}
            {!hasActive && (
              <li>
                <NavLink
                  to="/"
                  end
                  className="block rounded-xl px-4 py-4 text-[17px] font-semibold hover:bg-gray-50 outline-none focus:ring-2 focus:ring-blue-500"
                >
                  Back to Home
                </NavLink>
              </li>
            )}
            {profile?.is_admin && (
              <li>
                <NavLink
                  to="/admin"
                  end
                  className="block rounded-xl px-4 py-4 text-[17px] font-semibold hover:bg-gray-50 outline-none focus:ring-2 focus:ring-blue-500"
                >
                  Admin
                </NavLink>
              </li>
            )}
          </ul>
        </nav>
      </div>

      <div
        className={(acctOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none') +
          ' fixed inset-0 z-[60] bg-black/50 motion-safe:transition-opacity duration-200'}
        onClick={() => setAcctOpen(false)}
        aria-hidden="true"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Account menu"
        className={[
          'fixed top-0 right-0 z-[61] h-full w-[92%] max-w-sm',
          'bg-white text-gray-900 shadow-2xl',
          'rounded-l-2xl border border-gray-100',
          'motion-safe:transition-transform motion-safe:duration-300',
          acctOpen ? 'translate-x-0' : 'translate-x-full'
        ].join(' ')}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <img src="/images/scucci-core-icon.png" alt="" className="h-7 w-7" />
            <span className="text-xl font-semibold">Account</span>
          </div>
          <button
            type="button"
            aria-label="Close account menu"
            onClick={() => setAcctOpen(false)}
            className="h-10 w-10 inline-flex items-center justify-center rounded-lg hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        <div className="px-2 py-3">
          <p className="px-4 py-2 text-xs font-semibold uppercase tracking-widest text-gray-500">Manage</p>
          <div className="flex flex-col">
            <NavLink
              ref={acctFirstRef}
              to="/estate-sales-pipeline/member-maint2"
              end
              className="rounded-xl px-4 py-4 text-[17px] font-semibold hover:bg-gray-50 outline-none focus:ring-2 focus:ring-blue-500"
            >
              Update Membership
            </NavLink>
            <NavLink
              to="/estate-sales-pipeline/update-profile"
              end
              className="rounded-xl px-4 py-4 text-[17px] font-semibold hover:bg-gray-50 outline-none focus:ring-2 focus:ring-blue-500"
            >
              Update Account
            </NavLink>
            {profile?.is_admin && (
              <NavLink
                to="/admin"
                end
                className="rounded-xl px-4 py-4 text-[17px] font-semibold hover:bg-gray-50 outline-none focus:ring-2 focus:ring-blue-500"
              >
                Admin
              </NavLink>
            )}
            {user && (
              <>
                <div className="my-3 h-px bg-gray-200" aria-hidden="true" />
                <button
                  onClick={handleLogout}
                  className="text-left rounded-xl px-4 py-4 text-[17px] font-semibold text-red-600 hover:bg-red-50 outline-none focus:ring-2 focus:ring-red-500"
                >
                  Sign Out
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      <div aria-hidden="true" style={{ height: barHeight }} />
    </>
  );
}
